import React, { useState, useRef, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Play, Pause, ChevronLeft, Volume2, VolumeX, Music, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { useSite } from '@/context/SiteContext';
import PublicLayout from '@/components/layout/PublicLayout';
import { toast } from 'sonner';

const formatTime = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

const PlaylistPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { playlists, loading } = useSite();
  const [selectedTrack, setSelectedTrack] = useState(null);
  const [currentTrackId, setCurrentTrackId] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(75);
  const audioRef = useRef(null);

  const playlist = playlists.find(p => p.id === id);

  // Scroll to top immediately when page loads
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [id]);

  // Handle back to discography navigation
  const handleBackToDiscography = (e) => {
    e.preventDefault();
    navigate('/');
    // Wait for navigation then jump directly to music section
    setTimeout(() => {
      const musicSection = document.querySelector('#music');
      if (musicSection) {
        musicSection.scrollIntoView({ behavior: 'instant' });
      }
    }, 50);
  };

  // Handle audio time updates
  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  };

  // Handle audio loading errors
  const handleAudioError = (e) => {
    console.error('Audio error:', e);
    const audio = audioRef.current;
    if (audio && audio.error) {
      const errorCode = audio.error.code;
      let message = 'Unable to load audio';
      
      switch (errorCode) {
        case 1: // MEDIA_ERR_ABORTED
          message = 'Audio loading was aborted';
          break;
        case 2: // MEDIA_ERR_NETWORK
          message = 'Network error while loading audio';
          break;
        case 3: // MEDIA_ERR_DECODE
          message = 'Audio file could not be decoded';
          break;
        case 4: // MEDIA_ERR_SRC_NOT_SUPPORTED
          message = 'Audio format not supported';
          break;
        default:
          message = 'Unable to load audio file';
      }
      
      toast.error(message);
    }
    
    setIsPlaying(false);
    setCurrentTrackId(null);
  };

  const handleEnded = () => {
    // Auto advance to next track
    if (playlist?.tracks) {
      const currentIndex = playlist.tracks.findIndex(t => t.id === currentTrackId);
      if (currentIndex < playlist.tracks.length - 1) {
        const nextTrack = playlist.tracks[currentIndex + 1];
        if (nextTrack.audio_file) {
          playTrack(nextTrack);
        } else {
          setIsPlaying(false);
          setCurrentTrackId(null);
        }
      } else {
        setIsPlaying(false);
      }
    }
  };

  // Handle volume changes
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume / 100;
    }
  }, [volume]);

  // Play a specific track
  const playTrack = (track) => {
    if (!track.audio_file) return;
    
    const trackIndex = playlist?.tracks?.findIndex(t => t.id === track.id) ?? -1;
    
    setSelectedTrack(track);
    
    if (currentTrackId === track.id) {
      // Toggle play/pause for same track
      if (isPlaying) {
        audioRef.current?.pause();
        setIsPlaying(false);
      } else {
        audioRef.current?.play().catch(err => {
          console.error('Play error:', err);
          // Retry once
          setTimeout(() => {
            audioRef.current?.play().catch(console.error);
          }, 100);
        });
        setIsPlaying(true);
      }
    } else {
      // Play new track
      setCurrentTrackId(track.id);
      setCurrentTime(0);
      
      if (audioRef.current) {
        // Use stream endpoint for seeking support (Range requests)
        let audioUrl = track.audio_file;
        if (audioUrl.includes('/api/uploads/')) {
          audioUrl = audioUrl.replace('/api/uploads/', '/api/stream/');
        }
        
        // Add cache-busting parameter to force fresh fetch
        const cacheBuster = `?v=${Date.now()}`;
        audioUrl = audioUrl + cacheBuster;
        
        const audio = audioRef.current;
        audio.src = audioUrl;
        
        // For Safari compatibility, wait for canplay event before playing
        const handleCanPlay = () => {
          audio.removeEventListener('canplay', handleCanPlay);
          audio.removeEventListener('error', handleError);
          
          audio.play()
            .then(() => {
              setIsPlaying(true);
            })
            .catch(err => {
              console.error('Play error:', err);
              setIsPlaying(false);
              setCurrentTrackId(null);
              
              if (err.name === 'NotAllowedError') {
                toast.error('Tap the play button again to start playback');
              } else if (err.name === 'NotSupportedError') {
                toast.error('This audio format is not supported by your browser');
              } else {
                toast.error('Unable to play audio. Please try again.');
              }
            });
        };
        
        const handleError = () => {
          audio.removeEventListener('canplay', handleCanPlay);
          audio.removeEventListener('error', handleError);
          console.error('Audio load error');
          setIsPlaying(false);
          setCurrentTrackId(null);
          toast.error('Failed to load audio file');
        };
        
        audio.addEventListener('canplay', handleCanPlay);
        audio.addEventListener('error', handleError);
        audio.load();
      }
      
      // On mobile, scroll the track into view after a short delay for the progress bar to render
      setTimeout(() => {
        if (trackIndex >= 0 && window.innerWidth < 1024) {
          const trackRow = document.querySelector(`[data-testid="track-row-${trackIndex}"]`);
          if (trackRow) {
            const rect = trackRow.getBoundingClientRect();
            const offsetTop = window.scrollY + rect.top - 100;
            window.scrollTo({ top: Math.max(0, offsetTop), behavior: 'smooth' });
          }
        }
      }, 200);
    }
  };

  // Select track without playing (just show info) - toggles on mobile
  const selectTrack = (track, index) => {
    // If same track is clicked, toggle off (deselect)
    if (selectedTrack?.id === track.id) {
      setSelectedTrack(null);
    } else {
      setSelectedTrack(track);
      // On mobile, scroll the track row into view so description is visible
      // Use a timeout to let the description render first
      setTimeout(() => {
        const trackRow = document.querySelector(`[data-testid="track-row-${index}"]`);
        if (trackRow && window.innerWidth < 1024) {
          // Get the position and scroll with offset for fixed header + some padding
          const rect = trackRow.getBoundingClientRect();
          const offsetTop = window.scrollY + rect.top - 100; // 100px for header + extra padding
          window.scrollTo({ top: Math.max(0, offsetTop), behavior: 'smooth' });
        }
      }, 150);
    }
  };

  const seekTo = (percentage) => {
    const audio = audioRef.current;
    if (!audio) return;
    
    const duration = audio.duration;
    if (!duration || !isFinite(duration) || duration === 0) return;
    
    const newTime = (percentage / 100) * duration;
    if (isFinite(newTime) && newTime >= 0 && newTime <= duration) {
      audio.currentTime = newTime;
    }
  };

  const handleProgressBarClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clickX = clientX - rect.left;
    const percentage = Math.max(0, Math.min(100, (clickX / rect.width) * 100));
    seekTo(percentage);
  };

  if (loading) {
    return (
      <PublicLayout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-foreground"></div>
        </div>
      </PublicLayout>
    );
  }

  if (!playlist) {
    return (
      <PublicLayout>
        <div className="min-h-screen flex flex-col items-center justify-center gap-4">
          <p className="text-muted-foreground">Playlist not found</p>
          <Link to="/#music">
            <Button variant="outline">
              <ChevronLeft className="h-4 w-4 mr-2" />
              Back to Music
            </Button>
          </Link>
        </div>
      </PublicLayout>
    );
  }

  // Determine what to show on the right panel
  const displayImage = selectedTrack?.image || playlist.cover_image;
  const displayTitle = selectedTrack?.title || playlist.title;
  const displayDescription = selectedTrack?.description || playlist.description;

  const progress = (audioRef.current?.duration || duration) > 0 
    ? (currentTime / (audioRef.current?.duration || duration)) * 100 
    : 0;

  return (
    <PublicLayout>
      {/* Hidden audio element */}
      <audio
        ref={audioRef}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onEnded={handleEnded}
        onError={handleAudioError}
        preload="metadata"
      />

      <div className="min-h-screen pt-24 pb-16 bg-background">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          {/* Back Button */}
          <button 
            onClick={handleBackToDiscography}
            className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors group mb-8"
            data-testid="back-to-music"
          >
            <ChevronLeft className="h-5 w-5 group-hover:-translate-x-1 transition-transform" />
            <span className="text-sm">Back to Discography</span>
          </button>

          {/* Main Content - Two Column Layout */}
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-20">
            {/* Left Column - Track List */}
            <div className="order-2 lg:order-1 lg:pr-8">
              {/* Playlist Header (mobile) */}
              <div className="lg:hidden mb-6">
                <h1 className="font-serif text-3xl mb-2">{playlist.title}</h1>
                <p className="text-sm text-muted-foreground">{playlist.tracks?.length || 0} tracks</p>
              </div>

              {/* Track List Header */}
              <div className="hidden md:grid grid-cols-[40px_1fr_80px] gap-4 px-4 py-2 text-xs uppercase tracking-wider text-muted-foreground border-b border-border mb-2">
                <span>#</span>
                <span>Title</span>
                <span className="flex items-center justify-end">
                  <Clock className="h-4 w-4" />
                </span>
              </div>

              {/* Tracks */}
              <div className="space-y-1">
                {playlist.tracks?.map((track, index) => {
                  const isCurrentTrack = currentTrackId === track.id;
                  const isSelected = selectedTrack?.id === track.id;

                  return (
                    <div
                      key={track.id}
                      className={`group relative rounded-lg transition-colors ${
                        isSelected ? 'bg-secondary' : 'md:hover:bg-secondary/50 active:bg-secondary/50'
                      }`}
                      data-testid={`track-row-${index}`}
                    >
                      <div className="grid grid-cols-[40px_1fr_80px] gap-4 px-4 py-4 items-center">
                        {/* Play Button / Track Number */}
                        <button
                          onClick={() => playTrack(track)}
                          disabled={!track.audio_file}
                          className={`flex items-center justify-center ${!track.audio_file ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                        >
                          {isCurrentTrack && isPlaying ? (
                            <Pause className="h-4 w-4 text-foreground" />
                          ) : isCurrentTrack ? (
                            <Play className="h-4 w-4 text-foreground" />
                          ) : isSelected ? (
                            // Show play icon when track is selected (important for mobile)
                            <Play className="h-4 w-4 text-foreground" />
                          ) : (
                            <>
                              {/* On desktop: show number, show play on hover */}
                              <span className="hidden md:group-hover:hidden md:block text-muted-foreground">{index + 1}</span>
                              <Play className="h-4 w-4 md:hidden md:group-hover:block text-foreground" />
                            </>
                          )}
                        </button>

                        {/* Track Title - Clickable to show info */}
                        <button
                          onClick={() => selectTrack(track, index)}
                          className="text-left min-w-0"
                        >
                          <p className={`truncate ${isSelected ? 'text-foreground font-medium' : 'text-foreground/90'}`}>
                            {track.title}
                          </p>
                          {!track.audio_file && (
                            <p className="text-xs text-muted-foreground">Audio not uploaded</p>
                          )}
                        </button>

                        {/* Duration */}
                        <div className="text-right text-sm text-muted-foreground">
                          {formatTime(track.duration)}
                        </div>
                      </div>

                      {/* Inline Progress Bar (only for currently playing track) */}
                      {isCurrentTrack && (
                        <div className="px-4 pb-4 pt-0">
                          <div className="flex items-center gap-3">
                            <span className="text-xs text-muted-foreground w-10 text-right">
                              {formatTime(currentTime)}
                            </span>
                            <input
                              type="range"
                              min="0"
                              max="100"
                              step="0.1"
                              value={progress || 0}
                              onChange={(e) => seekTo(parseFloat(e.target.value))}
                              className="flex-1 h-2 bg-primary/20 rounded-full appearance-none cursor-pointer accent-primary [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-primary [&::-webkit-slider-thumb]:cursor-pointer [&::-moz-range-thumb]:w-5 [&::-moz-range-thumb]:h-5 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-white [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-primary [&::-moz-range-thumb]:cursor-pointer"
                              style={{
                                background: `linear-gradient(to right, hsl(var(--primary)) ${progress}%, hsl(var(--primary) / 0.2) ${progress}%)`
                              }}
                            />
                            <span className="text-xs text-muted-foreground w-10">
                              {formatTime(audioRef.current?.duration || duration || track.duration)}
                            </span>

                            {/* Volume Control */}
                            <div className="hidden sm:flex items-center gap-2 ml-2">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setVolume(volume === 0 ? 75 : 0);
                                }}
                              >
                                {volume === 0 ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
                              </Button>
                              <div className="w-20">
                                <Slider
                                  value={[volume]}
                                  max={100}
                                  step={1}
                                  onValueChange={(value) => setVolume(value[0])}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Mobile: Inline track description (shows when track is selected on mobile) */}
                      {isSelected && track.description && (
                        <div className="lg:hidden px-4 pb-4 border-t border-border/50 mt-2 pt-3">
                          {track.image && (
                            <img 
                              src={track.image} 
                              alt={track.title}
                              className="w-20 h-20 rounded-lg object-cover mb-3"
                            />
                          )}
                          <p className="text-sm text-muted-foreground leading-relaxed">
                            {track.description}
                          </p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {(!playlist.tracks || playlist.tracks.length === 0) && (
                <div className="text-center py-12">
                  <Music className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">No tracks in this playlist yet</p>
                </div>
              )}
            </div>

            {/* Right Column - Image and Description */}
            <div className="order-1 lg:order-2 lg:sticky lg:top-24 lg:self-start">
              {/* Image - Smaller size */}
              <div className="w-48 h-48 lg:w-64 lg:h-64 rounded-2xl overflow-hidden bg-muted shadow-2xl mb-6 mx-auto lg:mx-0">
                {displayImage ? (
                  <img 
                    src={displayImage} 
                    alt={displayTitle}
                    className="w-full h-full object-cover transition-opacity duration-300"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-muted to-muted-foreground/20">
                    <Music className="h-16 w-16 text-muted-foreground" />
                  </div>
                )}
              </div>

              {/* Title and Description */}
              <div className="space-y-6">
                <div>
                  {selectedTrack ? (
                    <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Now Viewing</p>
                  ) : (
                    <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Album</p>
                  )}
                  <h1 className="font-serif text-2xl lg:text-3xl">{displayTitle}</h1>
                </div>
                
                {displayDescription && (
                  <p className="text-muted-foreground leading-relaxed mt-4">
                    {displayDescription}
                  </p>
                )}

                {selectedTrack && (
                  <button
                    onClick={() => setSelectedTrack(null)}
                    className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                  >
                    ← View album info
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </PublicLayout>
  );
};

export default PlaylistPage;
